import { v4 as uuidv4 } from 'uuid';
import { PageResult } from "../../API/api";
import { EdullaConfig } from "../../config/configType";
import { ChatMessage } from "../Shared/API/ApiTypes";
import { ValuesOf } from '../../types';

export const PROACTIVE_TIMER_PHASE = {
    idle: 'idle',
    armed: 'armed',
    firing: 'firing',
    done: 'done',
} as const;

export type ProactiveTimerPhase = ValuesOf<typeof PROACTIVE_TIMER_PHASE>;

export const initialState: AppState = {
    enteredInformation: false,
    lastLocation: "/",
    pageUrl: "",
    shrunk: false,
    messages: [{ content: "Hello, how can I help you today?", direction: 1, time: new Date(Date.now()) }],
    relatedLinks: { newLinks: false, relatedLinks: [] },
    searchResults: { showSearchResults: false, showSearch: false, searching: false, searchResults: [], structuredResults: [], showLinks: false, searchQuery: "" },
    firstName: '',
    lastName: '',
    personalDetails: {},
    formDetails: {},
    filteredFormData: [],
    savedFormData: [],
    sessionid: uuidv4(),
    proactiveAttemptId: null,
    proactiveTimerPhase: PROACTIVE_TIMER_PHASE.idle,
    proactiveLastActivityAt: null,
    proactiveCurrentDelaySeconds: null,
    proactiveShownAt: null,
}

export type RelatedLinks = {
    newLinks: boolean,
    relatedLinks: PageResult[]
}

export type SearchResults = {
    showSearchResults: boolean,
    showSearch: boolean,
    searching: boolean,
    showLinks: boolean,
    searchQuery: string,
    searchResults: PageResult[],
    structuredResults: any[]
}

export type ContactInformation = {
    viewed: boolean,
    Title: string,
    Url: string,
    Email: string,
    Phone: string,
    Address: string
}

export type AppState = {
    enteredInformation: boolean,
    shrunk: boolean,
    lastLocation: string,
    pageUrl: string,
    messages: ChatMessage[]
    searchResults: SearchResults,
    relatedLinks: RelatedLinks,
    contactInformation?: ContactInformation
    firstName: string,
    lastName: string,
    personalDetails: any,
    formDetails: any,
    filteredFormData: any,
    savedFormData: any,
    sessionid: string,
    proactiveAttemptId: string | null,
    proactiveTimerPhase: ProactiveTimerPhase,
    proactiveLastActivityAt: number | null,
    proactiveCurrentDelaySeconds: number | null,
    // Timestamp (ms) when the most recent proactive message was rendered/shown
    // (ProactiveShown fired). Drives the implicit close-after-nudge dismiss
    // heuristic. Set on render; cleared only when a dismissal is consummated.
    proactiveShownAt: number | null,
}

export const getStorageIdentifier = () => {
    return storageIdentifier + sessionStorage.getItem("edullaTid")
}

export type AppAction =
    | { type: 'setEnteredInformation', hasEnteredInformation: boolean }
    | { type: 'setShrunk', shrunk: boolean }
    | { type: 'addMessage', message: ChatMessage }
    | { type: 'updateTempMessage', message: ChatMessage }
    | { type: 'removeMessage', index: number }
    | { type: 'reconcileTurn', turnId: string, message: ChatMessage }
    | { type: 'setRelatedLinks', relatedLinks: PageResult[] }
    | { type: 'setSearchResults', searchResults: PageResult[], structuredResults: any[], showSearchResults: boolean, showSearch: boolean, searching: boolean, showLinks: boolean, searchQuery: string }
    | { type: 'setShowSearchResults', showSearchResults: boolean }
    | { type: 'setShowSearch', showSearch: boolean }
    | { type: 'setSearchQuery', searchQuery: string }
    | { type: 'setShowSearchLinks', showSearchLinks: boolean }
    | { type: 'setRelatedLinksViewed', viewed: boolean }
    | { type: 'setContactInformationFromLinks', links: PageResult[], config: EdullaConfig }
    | { type: 'setContactInformationViewed', viewed: boolean }
    | { type: 'setFirstName', firstName: string }
    | { type: 'setLastName', lastName: string }
    | { type: 'setLastLocation', location: string }
    | { type: 'setPersonalDetails', personalDetails: any }
    | { type: 'setFormDetails', formDetails: any }
    | { type: 'setFilteredFormData', filteredFormData: any }
    | { type: 'addSavedFormData', savedFormData: any }
    | { type: 'setPageUrl', pageUrl: string }
    | { type: 'armProactiveTimer' }
    | { type: 'fireProactiveTimer' }
    | { type: 'renderProactiveMessage', message: ChatMessage }
    | { type: 'rearmProactiveTimer', nextDelaySeconds?: number }
    | { type: 'suppressProactiveTimer' }
    | { type: 'dismissProactive' }
    | { type: 'doneProactive' }
    | { type: 'setProactiveLastActivityAt', timestamp: number }

export const storageIdentifier = "edullaLocalApp"

/**
 * The single place a temp (typing/streaming) bubble becomes its resolved
 * message. The incoming message is spread over the retained bubble, so every
 * field the incoming message carries — including any per-turn
 * conversational-forms field added to ChatMessage later — flows through
 * automatically instead of having to be hand-listed per field. (Hand-listing
 * ate two bugs: a dropped fills payload and a fillNotice near-miss; and
 * updateTempMessage carried only `fills`, a latent instance of the same
 * class.) `retain` pins the few bubble fields a caller must keep, e.g. the
 * content accumulated while streaming. Always returns a new object — callers
 * must not mutate the existing array element.
 */
const resolveTempMessage = (
    tempMessage: ChatMessage,
    message: ChatMessage,
    retain?: Partial<ChatMessage>
): ChatMessage => ({
    ...tempMessage,
    ...message,
    ...retain,
    temp: false,
});

const addNewMessage = (messages: ChatMessage[], message: ChatMessage): ChatMessage[] => {
    if (message.direction === 1 && messages.length > 0) {
        const lastIndex = messages.length - 1;
        const lastMessage = messages[lastIndex];
        if (lastMessage.temp) {
            const resolved = resolveTempMessage(lastMessage, message);
            return [...messages.slice(0, lastIndex), resolved].filter(m => !m.temp);
        } else {
            return [...messages.filter(m => !m.temp), message]
        }
    }
    else {
        return [...messages, message]
    }
}


const updateTempMessage = (messages: ChatMessage[], message: ChatMessage): ChatMessage[] => {
    if (message.direction === 1 && messages.length > 0) {
        const lastIndex = messages.length - 1;
        const lastMessage = messages[lastIndex];
        if (lastMessage.temp && !message.relatedLinks) {
            // Mid-stream chunk: append to the accumulating bubble; it stays temp.
            const accumulated: ChatMessage = {
                ...lastMessage,
                content: lastMessage.content + message.content,
                time: message.time,
                relatedLinks: message.relatedLinks,
                cards: message.cards,
                temp: true,
            };
            return [...messages.slice(0, lastIndex), accumulated];
        } else if (lastMessage.temp && message.relatedLinks) {
            // The stream's terminal event closes out the bubble. It carries the
            // turn's payloads (fills, notices, offers, …), so it resolves through
            // the shared merge — only the accumulated streamed content survives
            // from the bubble itself. Guarded on `lastMessage.temp` (like the
            // branch above) so a terminal-shaped message arriving with no temp
            // bubble to close never overwrites the last resolved message in
            // history instead of being appended as a new one.
            const resolved = resolveTempMessage(lastMessage, message, { content: lastMessage.content });
            return [...messages.slice(0, lastIndex), resolved].filter(m => !m.temp);
        } else {
            return [...messages.filter(m => !m.temp), message]
        }
    }
    else {
        return [...messages, message]
    }
}

const parseContactInformationFromLinks = (links: PageResult[], config: EdullaConfig) => {
    const contactInfoAll = config.ContactInformation
    const re = new RegExp("^([a-z][a-z0-9+-.]*:(//[^/?#]+)?)?(/?[a-z0-9-._~%!$&'()*+,;=@]+)");
    if (contactInfoAll && contactInfoAll.length > 0 && links) {
        const baseUrls = links.map((l) => {
            let match = l.url.match(re);
            if (match && match.length > 0) {
                return match[0]
            }
            else {
                return ""
            }
        });
        let compare = "";
        let mostFreq = "";

        baseUrls.reduce((acc: any, val) => {
            if (val in acc) {               // if key already exists
                acc[val]++;                // then increment it by 1
            } else {
                acc[val] = 1;      // or else create a key with value 1
            }
            if (acc[val] > compare) {   // if value of that key is greater
                // than the compare value.
                compare = acc[val];    // than make it a new compare value.
                mostFreq = val;        // also make that key most frequent.
            }
            return acc;
        }, {})
        let result = contactInfoAll.find((c) => c.Url === mostFreq);
        return result;
    }
    return undefined
}

export const appReducer = (state: AppState, action: AppAction): AppState => {
    switch (action.type) {
        case "setEnteredInformation":
            return { ...state, enteredInformation: action.hasEnteredInformation };
        case "setShrunk":
            return { ...state, shrunk: action.shrunk };
        case "addMessage":
            return { ...state, messages: addNewMessage(state.messages, action.message) };
        case "updateTempMessage":
            return { ...state, messages: updateTempMessage(state.messages, action.message) };
        case "removeMessage":
            // Used by the conversational-forms retry: the failed turn's error
            // bubble is removed while the user's own message stays put.
            if (action.index < 0 || action.index >= state.messages.length) return state;
            return { ...state, messages: state.messages.filter((_, i) => i !== action.index) };
        case "reconcileTurn": {
            // A turn that timed out and then resolved successfully (store
            // tracker 6.29): the real reply replaces the timeout bubble in
            // place. Only while that bubble still exists — a Try again already
            // removed it, and a reply must never render twice — so a missing
            // id is a silent no-op. Keyed by id, not index: indices shift as
            // messages come and go while the response is late.
            const idx = state.messages.findIndex(
                (m) => m.formError === true && m.turnId === action.turnId
            );
            if (idx === -1) return state;
            const messages = [...state.messages];
            messages[idx] = action.message;
            return { ...state, messages };
        }
        case "setRelatedLinks":
            return { ...state, relatedLinks: { newLinks: true, relatedLinks: action.relatedLinks } };
        case "setSearchResults":
            return { ...state, searchResults: { showSearchResults: action.showSearchResults, searchResults: action.searchResults, structuredResults: action.structuredResults, showSearch: action.showSearch, searching: action.searching, showLinks: action.showLinks, searchQuery: action.searchQuery } };
        case "setShowSearchResults":
            return { ...state, searchResults: { ...state.searchResults, showSearchResults: action.showSearchResults } };
        case "setShowSearch":
            return { ...state, searchResults: { ...state.searchResults, showSearch: action.showSearch } };
        case "setShowSearchLinks":
            return { ...state, searchResults: { ...state.searchResults, showLinks: action.showSearchLinks } };
        case "setSearchQuery":
            return { ...state, searchResults: { ...state.searchResults, searchQuery: action.searchQuery } };
        case "setRelatedLinksViewed":
            return { ...state, relatedLinks: { newLinks: !action.viewed, relatedLinks: state.relatedLinks.relatedLinks } };
        case "setContactInformationFromLinks":
            return { ...state, contactInformation: parseContactInformationFromLinks(action.links, action.config) };
        case "setContactInformationViewed":
            if (state.contactInformation) {
                return { ...state, contactInformation: { Title: state.contactInformation?.Title, Url: state.contactInformation?.Url, Email: state.contactInformation?.Email, Phone: state.contactInformation?.Phone, Address: state.contactInformation?.Address, viewed: action.viewed } };
            }
            return state;
        case "setFirstName":
            return { ...state, firstName: action.firstName };
        case "setLastName":
            return { ...state, lastName: action.lastName };
        case "setLastLocation":
            return { ...state, lastLocation: action.location };
        case "setPersonalDetails":
            return { ...state, personalDetails: action.personalDetails };
        case "setFormDetails":
            return { ...state, formDetails: action.formDetails };
        case "setFilteredFormData":
            return { ...state, filteredFormData: action.filteredFormData };
        case "addSavedFormData":
            if (state.savedFormData.includes(action.savedFormData)) {
                return { ...state, savedFormData: [...state.savedFormData.filter((item: any) => item !== action.savedFormData)] }
            }
            else {
                return { ...state, savedFormData: [...state.savedFormData, action.savedFormData] }
            }
        case "setPageUrl":
            return { ...state, pageUrl: action.pageUrl };
        case "armProactiveTimer":
            if ((state.proactiveTimerPhase ?? PROACTIVE_TIMER_PHASE.idle) !== PROACTIVE_TIMER_PHASE.idle) return state;
            return { ...state, proactiveTimerPhase: PROACTIVE_TIMER_PHASE.armed, proactiveAttemptId: uuidv4() };
        case "fireProactiveTimer":
            if (state.proactiveTimerPhase !== PROACTIVE_TIMER_PHASE.armed) return state;
            return { ...state, proactiveTimerPhase: PROACTIVE_TIMER_PHASE.firing };
        case "renderProactiveMessage":
            return {
                ...state,
                messages: addNewMessage(state.messages, action.message),
                proactiveShownAt: new Date(action.message.time).getTime(),
            };
        case "rearmProactiveTimer":
            return {
                ...state,
                proactiveTimerPhase: PROACTIVE_TIMER_PHASE.armed,
                proactiveAttemptId: uuidv4(),
                proactiveLastActivityAt: Date.now(),
                proactiveCurrentDelaySeconds: action.nextDelaySeconds !== undefined ? action.nextDelaySeconds : state.proactiveCurrentDelaySeconds,
            };
        case "suppressProactiveTimer":
            if (state.proactiveTimerPhase !== PROACTIVE_TIMER_PHASE.firing) return state;
            return { ...state, proactiveTimerPhase: PROACTIVE_TIMER_PHASE.armed };
        case "dismissProactive":
            // A consummated dismissal: stop arming AND clear proactiveShownAt so a
            // subsequent widget close cannot re-fire an implicit dismissal for the
            // same nudge.
            return { ...state, proactiveTimerPhase: PROACTIVE_TIMER_PHASE.done, proactiveShownAt: null };
        case "doneProactive":
            // Normal post-render terminal transition. Keep proactiveShownAt intact so
            // the implicit close-after-nudge window stays open after a cap-1 render.
            return { ...state, proactiveTimerPhase: PROACTIVE_TIMER_PHASE.done };
        case "setProactiveLastActivityAt":
            return { ...state, proactiveLastActivityAt: action.timestamp };
        default:
            return state;
    }

};
